import { useState } from 'react';
import Modal from '../shared/Modal';

// "Sign out everywhere" for the client and contractor portals (pen test
// AD26341, AD8 "Session Management"). A portal login can be signed in on more
// than one device; this ends every one of its sessions, this browser's too,
// so somebody who has the password or a copied session is put out at once.
// The server side is POST /api/client/logout-all and /api/resource/logout-all
// in src/api/casual.js.
//
// The confirmation says what will happen, and a failure is shown in the dialog
// rather than somewhere else on the page.

export const SIGNED_OUT_EVERYWHERE = 'You are signed out of the portal on every device. Sign in again to carry on.';

export default function SignOutEverywhere({ endpoint, onSignedOut, className = '', label = 'Sign out everywhere' }) {
  const [state, setState] = useState(null); // null | { busy, error }

  const confirm = async () => {
    setState({ busy: true, error: '' });
    try {
      const res = await fetch(`/api/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        // Already signed out here (timed out, or ended from another device):
        // the outcome the person asked for, so carry on to the sign-in screen.
        if (res.status !== 401) throw new Error(data?.error || `The portal did not answer (${res.status}). Try again.`);
      }
    } catch (e) {
      setState({ busy: false, error: e.message || 'You could not be signed out everywhere. Try again.' });
      return;
    }
    setState(null);
    onSignedOut(SIGNED_OUT_EVERYWHERE);
  };

  return (
    <>
      <button type="button" onClick={() => setState({ busy: false, error: '' })} className={className}>
        {label}
      </button>
      {state && (
        <Modal
          title="Sign out everywhere"
          onClose={() => { if (!state.busy) setState(null); }}
          footer={
            <div className="flex gap-2 justify-end w-full">
              <button type="button" onClick={() => setState(null)} disabled={state.busy}
                className="px-3 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Stay signed in
              </button>
              <button type="button" onClick={confirm} disabled={state.busy}
                className="px-3 py-1.5 text-[11px] font-semibold rounded-md bg-navy text-white disabled:opacity-50">
                {state.busy ? 'Signing out…' : 'Sign out everywhere'}
              </button>
            </div>
          }
        >
          <div className="space-y-2 text-[12px] text-gray-600 leading-relaxed">
            <p>
              This signs you out of the portal on every computer, phone and browser, including this one.
              Use it if you signed in on a device you no longer use, or think somebody else could be using your account.
            </p>
            <p>
              You will need your password to sign in again. If you think somebody else knows it, change it once you
              are back in.
            </p>
            {state.error && (
              <p role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">{state.error}</p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
